import React from "react";
import { Box, Button, Card, Icon, Input, Text } from "@salestrak/ui";

import { useDeleteConnection } from "./use-connection-config";

// Type-the-name destructive confirmation for deleting a saved connection.
//
// Rendered INLINE inside the fullscreen Integrations dialog rather than as a
// nested AlertDialog: a modal-within-a-modal loses the focus fight with
// the parent dialog's focus scope, so the confirm input can't be typed into.
// Living in the same focus scope as the rest of the dialog sidesteps that
// entirely — the input is just another field on the page.
export function DeleteConnectionConfirm({
  connection,
  providerId,
  onCancel,
  onDeleted,
}: {
  connection: { id: string; name: string };
  providerId: string;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const [confirmName, setConfirmName] = React.useState("");
  const { deleting, handleDelete } = useDeleteConnection(connection.id, providerId, onDeleted);

  const nameMatches = confirmName.trim() === connection.name;

  // Enter in the confirm field submits the delete — but only when it would be
  // allowed (name matches, not already deleting), matching the button's guard.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && nameMatches && !deleting) void handleDelete();
  };

  return (
    <Card fill="width" gap="md" padding="lg" radius="lg">
      <Box align="center" direction="row" gap="sm">
        <Icon color="destructive" name="trash" />
        <Text weight="bold">
          Delete &ldquo;{connection.name}&rdquo;?
        </Text>
      </Box>
      <Text size="sm" variant="muted">
        This connection and its stored credentials will be permanently removed.
        This action cannot be undone.
      </Text>
      <Input
        autoCapitalize="none"
        placeholder={`Type "${connection.name}" to confirm`}
        value={confirmName}
        onChange={(e) => setConfirmName(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <Box direction="row" gap="sm" justify="end">
        <Button disabled={deleting} size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={deleting || !nameMatches}
          loading={deleting}
          size="sm"
          color="destructive"
          onClick={handleDelete}
        >
          Delete connection
        </Button>
      </Box>
    </Card>
  );
}
